import { supportTicketService } from "./SupportTicketService";
import { SupportAIService } from "./SupportAIService";
import { supabase } from "@core/lib/supabase";
import { invokeSupabaseFunction } from "@core/lib/supabaseApi";

export type TicketSeverity = "low" | "normal" | "high" | "critical" | "blocker";

export interface SLAAlert {
  ticketId: string;
  ticketTitle: string;
  severity: TicketSeverity;
  overdueDays: number;
  dueDate: Date;
  assignee?: string;
  priority: "low" | "medium" | "high" | "critical";
}

export interface SLAMetrics {
  total: number;
  overdue: number;
  withinSLA: number;
  complianceRate: number;
  byPriority: {
    blocker: { total: number; overdue: number };
    critical: { total: number; overdue: number };
    normal: { total: number; overdue: number };
    low: { total: number; overdue: number };
  };
}

/**
 * Service pour la gestion des notifications SLA
 * Détecte les tickets qui dépassent leur SLA et envoie des alertes
 */
export class SLANotificationService {
  private static instance: SLANotificationService;
  private checkInterval: number | null = null;
  private alerts: SLAAlert[] = [];
  private lastCheck: Date | null = null;
  private notifiedTickets = new Set<string>(); // Tickets déjà notifiés
  private lastResetDate = new Date().toDateString(); // Date de dernière réinitialisation
  private readonly STORAGE_KEY = "sla_notified_tickets"; // Clé localStorage

  private constructor() {
    this.alerts = [];
    this.lastCheck = null;
    this.loadNotifiedTickets(); // Charger depuis localStorage au démarrage
    this.resetDailyNotifications(); // Réinitialiser au démarrage
  }

  static getInstance(): SLANotificationService {
    if (!SLANotificationService.instance) {
      SLANotificationService.instance = new SLANotificationService();
    }
    return SLANotificationService.instance;
  }

  /**
   * Charge les tickets notifiés depuis localStorage
   */
  private loadNotifiedTickets(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.notifiedTickets = new Set(data.tickets || []);
        this.lastResetDate = data.date || new Date().toDateString();
        console.log(
          `📥 ${this.notifiedTickets.size} tickets notifiés chargés depuis localStorage`,
        );
      }
    } catch (error) {
      console.warn("Erreur chargement notifications SLA:", error);
    }
  }

  /**
   * Sauvegarde les tickets notifiés dans localStorage
   */
  private saveNotifiedTickets(): void {
    try {
      const data = {
        tickets: Array.from(this.notifiedTickets),
        date: this.lastResetDate,
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Erreur sauvegarde notifications SLA:", error);
    }
  }

  /**
   * Réinitialise les notifications quotidiennes
   */
  private resetDailyNotifications(): void {
    const today = new Date().toDateString();
    if (this.lastResetDate !== today) {
      this.notifiedTickets.clear();
      this.lastResetDate = today;
      this.saveNotifiedTickets(); // Sauvegarder la réinitialisation
      console.log("🔄 SLA notifications réinitialisées pour la journée");
    }
  }

  /**
   * Démarre la surveillance automatique des SLA
   * @param intervalMinutes Intervalle de vérification en minutes
   */
  startMonitoring(intervalMinutes: number = 15): void {
    if (this.checkInterval) {
      console.log("🔄 SLA monitoring déjà actif, arrêt du précédent...");
      clearInterval(this.checkInterval);
    }

    console.log(
      `🚀 Démarrage monitoring SLA toutes les ${intervalMinutes} minutes`,
    );
    this.checkInterval = window.setInterval(
      () => {
        console.log(
          `⏰ Vérification SLA automatique (${new Date().toLocaleTimeString()})`,
        );
        this.checkSLACompliance();
      },
      intervalMinutes * 60 * 1000,
    );

    // Vérification immédiate au démarrage
    console.log("🔍 Première vérification SLA au démarrage");
    this.checkSLACompliance();
  }

  /**
   * Arrête la surveillance automatique
   */
  stopMonitoring(): void {
    if (this.checkInterval) {
      window.clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Vérifie la conformité SLA de tous les tickets ouverts
   */
  async checkSLACompliance(): Promise<SLAAlert[]> {
    try {
      // Réinitialiser si nouvelle journée
      this.resetDailyNotifications();

      const tickets = await supportTicketService.listTickets({});
      const openTickets = tickets.filter(
        (ticket) => ticket.status !== "closed",
      );

      const now = new Date();
      const newAlerts: SLAAlert[] = [];

      openTickets.forEach((ticket) => {
        const slaDays = SupportAIService.getSLADays(
          ticket.severity as TicketSeverity,
        );
        const createdDate = new Date(ticket.created_at);
        const dueDate = new Date(
          createdDate.getTime() + slaDays * 24 * 60 * 60 * 1000,
        );

        const overdueDays = Math.floor(
          (now.getTime() - dueDate.getTime()) / (24 * 60 * 60 * 1000),
        );

        // Créer une alerte si le ticket est en retard ET pas encore notifié
        if (overdueDays >= 0 && !this.notifiedTickets.has(ticket.id)) {
          const alert: SLAAlert = {
            ticketId: ticket.id,
            ticketTitle: ticket.subject,
            severity: ticket.severity as TicketSeverity,
            overdueDays,
            dueDate,
            assignee: undefined, // SupportTicket n'a pas de champ assignee
            priority: this.getAlertPriority(
              ticket.severity as TicketSeverity,
              overdueDays,
            ),
          };

          newAlerts.push(alert);
          this.notifiedTickets.add(ticket.id); // Marquer comme notifié
          this.saveNotifiedTickets(); // Sauvegarder dans localStorage
        }
      });

      this.lastCheck = now;

      // Envoyer SEULEMENT les nouvelles alertes
      if (newAlerts.length > 0) {
        console.log(
          `🚨 ${newAlerts.length} NOUVELLES alertes SLA détectées:`,
          newAlerts.map((a) => `${a.ticketTitle} (${a.overdueDays}j retard)`),
        );
        await this.sendAlerts(newAlerts);
        console.log(`📨 ${newAlerts.length} nouvelles alertes SLA envoyées`);
      } else {
        console.log(
          `✅ Aucune nouvelle alerte SLA (${this.notifiedTickets.size} tickets déjà notifiés)`,
        );
      }

      // Mettre à jour la liste des alertes pour le dashboard
      this.alerts = newAlerts;
      return this.alerts;
    } catch (error) {
      console.error("Error checking SLA compliance:", error);
      return [];
    }
  }

  /**
   * Calcule les métriques SLA pour le dashboard
   */
  async calculateSLAMetrics(): Promise<SLAMetrics> {
    try {
      const tickets = await supportTicketService.listTickets({});
      const activeTickets = tickets.filter(
        (ticket) => ticket.status !== "closed",
      );

      const metrics: SLAMetrics = {
        total: activeTickets.length,
        overdue: 0,
        withinSLA: 0,
        complianceRate: 0,
        byPriority: {
          blocker: { total: 0, overdue: 0 },
          critical: { total: 0, overdue: 0 },
          normal: { total: 0, overdue: 0 },
          low: { total: 0, overdue: 0 },
        },
      };

      const now = new Date();

      activeTickets.forEach((ticket) => {
        const slaDays = SupportAIService.getSLADays(
          ticket.severity as TicketSeverity,
        );
        const createdDate = new Date(ticket.created_at);
        const dueDate = new Date(
          createdDate.getTime() + slaDays * 24 * 60 * 60 * 1000,
        );

        const isOverdue = now.getTime() > dueDate.getTime();

        // Mettre à jour les compteurs par priorité
        const priorityKey = ticket.severity as keyof typeof metrics.byPriority;
        if (metrics.byPriority[priorityKey]) {
          metrics.byPriority[priorityKey].total++;
        }

        if (isOverdue) {
          metrics.overdue++;
          if (metrics.byPriority[priorityKey]) {
            metrics.byPriority[priorityKey].overdue++;
          }
        } else {
          metrics.withinSLA++;
        }
      });

      metrics.complianceRate =
        metrics.total > 0
          ? Math.round((metrics.withinSLA / metrics.total) * 100)
          : 0;

      return metrics;
    } catch (error) {
      console.error("Error calculating SLA metrics:", error);
      return {
        total: 0,
        overdue: 0,
        withinSLA: 0,
        complianceRate: 0,
        byPriority: {
          blocker: { total: 0, overdue: 0 },
          critical: { total: 0, overdue: 0 },
          normal: { total: 0, overdue: 0 },
          low: { total: 0, overdue: 0 },
        },
      };
    }
  }

  /**
   * Retourne les alertes actuelles
   */
  getCurrentAlerts(): SLAAlert[] {
    return this.alerts;
  }

  /**
   * Retourne la date de dernière vérification
   */
  getLastCheck(): Date | null {
    return this.lastCheck;
  }

  /**
   * Détermine la priorité de l'alerte en fonction de la sévérité et du retard
   */
  private getAlertPriority(
    severity: TicketSeverity,
    overdueDays: number,
  ): "low" | "medium" | "high" | "critical" {
    if (severity === "blocker" && overdueDays >= 1) return "critical";
    if (severity === "critical" && overdueDays >= 2) return "critical";
    if (severity === "normal" && overdueDays >= 5) return "high";
    if (severity === "low" && overdueDays >= 10) return "medium";

    if (overdueDays >= 7) return "high";
    if (overdueDays >= 3) return "medium";

    return "low";
  }

  /**
   * Envoie les alertes (console + Slack + Email)
   */
  private async sendAlerts(alerts: SLAAlert[]): Promise<void> {
    const criticalAlerts = alerts.filter(
      (alert) => alert.priority === "critical",
    );
    const highAlerts = alerts.filter((alert) => alert.priority === "high");

    // Console logging
    if (criticalAlerts.length > 0) {
      console.warn(
        `🚨 CRITICAL SLA ALERTS (${criticalAlerts.length}):`,
        criticalAlerts,
      );
    }

    if (highAlerts.length > 0) {
      console.warn(`⚠️ HIGH SLA ALERTS (${highAlerts.length}):`, highAlerts);
    }

    if (alerts.length > 0) {
      console.log(
        `📊 SLA Summary: ${alerts.length} tickets overdue (${criticalAlerts.length} critical, ${highAlerts.length} high)`,
      );
    }

    // Envoyer les alertes Slack (nouveau système intelligent)
    await this.sendSlackAlerts(alerts);

    // Envoyer les emails (via Edge Function)
    await this.sendEmailAlerts(alerts);
  }

  /**
   * Envoie les alertes vers Slack (agrégées avec liens)
   */
  private async sendSlackAlerts(alerts: SLAAlert[]): Promise<void> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      let message: string;

      if (alerts.length === 1) {
        // Une seule alerte : format normal
        message = this.formatSlackAlert(alerts[0]);
      } else {
        // Plusieurs alertes : format agrégé avec liens
        const ticketLinks = alerts
          .map((alert) => {
            const baseUrl = "https://doodates.vercel.app/admin"; // URL de ton dashboard
            return `• <${baseUrl}?ticket=${alert.ticketId}|${alert.ticketTitle}> (${alert.overdueDays}j retard)`;
          })
          .join("\n");

        message = `🚨 ${alerts.length} tickets SLA en retard:\n\n${ticketLinks}`;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn(
          "[SLANotificationService] Pas de session admin, skip Slack alert",
        );
        return;
      }

      try {
        await invokeSupabaseFunction(
          "send-slack-alert",
          {
            message,
            priority: alerts.some((a) => a.priority === "critical")
              ? "critical"
              : "high",
          },
          { timeout: 5000 },
        );
        console.log(`✅ Slack alert envoyée : ${alerts.length} ticket(s)`);
      } catch (e) {
        console.error("Failed to send Slack alert:", e);
      }
    } catch (error) {
      console.error("Error sending Slack alerts:", error);
    }
  }

  /**
   * Envoie les emails via Edge Function
   */
  private async sendEmailAlerts(alerts: SLAAlert[]): Promise<void> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn(
          "[SLANotificationService] Pas de session admin, skip SLA email alerts",
        );
        return;
      }

      for (const alert of alerts) {
        try {
          await invokeSupabaseFunction(
            "send-sla-alert",
            {
              ticketId: alert.ticketId,
              ticketTitle: alert.ticketTitle,
              severity: alert.severity,
              overdueDays: alert.overdueDays,
              priority: alert.priority,
            },
            { timeout: 10000 },
          );
        } catch (e) {
          console.error("Failed to send email alert:", e);
        }
      }
    } catch (error) {
      console.error("Error sending email alerts:", error);
    }
  }

  /**
   * Formate un message d'alerte pour Slack
   */
  formatSlackAlert(alert: SLAAlert): string {
    const emoji = this.getPriorityEmoji(alert.priority);
    const slaDays = this.getSLADays(alert.severity);

    return (
      `${emoji} *SLA Alert* - ${alert.ticketTitle}\n` +
      `📅 Overdue by ${alert.overdueDays} days (SLA: ${slaDays} days)\n` +
      `🔗 Ticket: ${alert.ticketId}\n` +
      `👤 Assignee: ${alert.assignee || "Unassigned"}\n` +
      `📊 Priority: ${alert.priority.toUpperCase()}`
    );
  }

  /**
   * Get SLA days for severity
   */
  private getSLADays(severity: TicketSeverity): number {
    switch (severity) {
      case "blocker":
        return 1;
      case "critical":
        return 2;
      case "normal":
        return 5;
      case "low":
        return 10;
      default:
        return 5;
    }
  }

  /**
   * Retourne l'emoji approprié pour la priorité
   */
  private getPriorityEmoji(priority: string): string {
    switch (priority) {
      case "critical":
        return "🚨";
      case "high":
        return "⚠️";
      case "medium":
        return "⚡";
      case "low":
        return "📝";
      default:
        return "📋";
    }
  }
}

// Export singleton
export const slaNotificationService = SLANotificationService.getInstance();
